import React, { useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button, message } from "antd";
import { PlusCircleOutlined, EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
// import { queryFilterParams, getSorterParams } from "../../../utils";
import { useNavigate, useParams, useSearchParams } from "react-router";
import Table from "../../../../Common/Table";
import {
  useExerciseCreate,
  useExerciseTableGet,
  useExerciseUpdate,
} from "../../../../../hooks/api/exercises";
import {
  queryFilterParams,
  getSorterParams,
  getUserRequestPath,
} from "../../../../../utils";
import ExerciseModal from "../ExerciseModal";
import { useAuth, UserRoleType } from "../../../../../store/auth";

export interface ExerciseTableRow {
  key: string;
  id: string;
  name: string;
}

export const columns = (): ProColumns<ExerciseTableRow>[] => [
  {
    title: "Avodot Name",
    dataIndex: "name",
    key: "name",
    hideInSearch: true,
  },
];

function ToolBar({ onCreate }: { onCreate: () => void }) {
  const { user } = useAuth();

  if (
    !user?.role ||
    getUserRequestPath(user.role as UserRoleType) !== "admin"
  ) {
    return null;
  }

  return (
    <Button
      key="create"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      Add Avodot
    </Button>
  );
}

export default function ExercisesTable(): React.ReactNode | null {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const exerciseTableGet = useExerciseTableGet();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingExercise, setEditingExercise] = useState<
    { id?: string; name: string } | undefined
  >();
  const exerciseCreate = useExerciseCreate();
  const exerciseUpdate = useExerciseUpdate();
  const isAdmin = user?.role
    ? getUserRequestPath(user.role as UserRoleType) === "admin"
    : false;

  const handleCreate = () => {
    setModalMode("create");
    setEditingExercise(undefined);
    setModalOpen(true);
  };

  const handleEdit = (record: ExerciseTableRow) => {
    setModalMode("edit");
    setEditingExercise({ name: record.name, id: record.id });
    setModalOpen(true);
  };

  const handleSave = async (values: { name: string }) => {
    try {
      if (modalMode === "create") {
        await exerciseCreate.fetch({
          name: values.name,
          middah: { id: id || "" },
        });
        message.success("New avoda has been added successfully");
      } else if (modalMode === "edit") {
        await exerciseUpdate.fetch(
          {
            name: values.name,
          },
          editingExercise?.id,
        );
        message.success("Avoda name has been changed successfully");
      }
      setTableKey(Date.now());
      setModalOpen(false);
    } catch (error: any) {
      message.error(error?.message || "Something went wrong");
    }
  };

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ExerciseTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });
    return exerciseTableGet
      .fetch({
        ...newParams,
        middahId: id,
      })
      .then((response) => {
        if (response) {
          return {
            data: response.data,
            success: true,
            total: response.total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };
  const actions: [
    string,
    (props: {
      text: React.ReactNode;
      record: ExerciseTableRow;
      id: number;
    }) => any,
    (props: {
      text: React.ReactNode;
      record: ExerciseTableRow;
      id: number;
    }) => boolean,
  ][] = isAdmin
      ? [
        [
          "edit",
          ({
            record,
          }: {
            text: React.ReactNode;
            record: ExerciseTableRow;
            id: number;
          }) => ({
            title: "Edit exercise",
            onClick: (e: MouseEvent) => {
              e.stopPropagation();
              handleEdit(record);
            },
          }),
          () => true,
        ],
      ]
      : [];

  return (
    <>
      <Table<ExerciseTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.exercises.tableName")}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={handleCreate} />,
        ]}
        request={tableRequest}
        columns={columns()}
        {...(isAdmin ? { actions } : {})}
        search={{
          style: {
            display: "none",
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by exercise name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-exercises",
          persistenceType: "localStorage",
        }}
      />
      <ExerciseModal
        open={modalOpen}
        mode={modalMode}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialValues={editingExercise}
      />
    </>
  );
}
